import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { SlidersHorizontal, Save, CalendarX, Plus, Trash2, ShieldAlert, Building2 } from "lucide-react";
import { toast } from "sonner";

const DAYS = [
  { idx: 0, label: "Lun" },
  { idx: 1, label: "Mar" },
  { idx: 2, label: "Mié" },
  { idx: 3, label: "Jue" },
  { idx: 4, label: "Vie" },
  { idx: 5, label: "Sáb" },
  { idx: 6, label: "Dom" },
];

export default function SettingsPage() {
  const { workshop, refresh } = useAuth();
  const [ratio, setRatio] = useState(60);
  const [quickRatio, setQuickRatio] = useState(60);
  const [workDays, setWorkDays] = useState([0, 1, 2, 3, 4]);
  const [saving, setSaving] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [hDate, setHDate] = useState("");
  const [hLabel, setHLabel] = useState("");
  const [wsName, setWsName] = useState("");
  const [savingName, setSavingName] = useState(false);

  const load = useCallback(async () => {
    const [s, h] = await Promise.all([api.get("/settings"), api.get("/holidays")]);
    setRatio(Math.round(s.data.ratio * 100));
    setQuickRatio(Math.round((s.data.quick_ratio ?? 0.6) * 100));
    setWorkDays(s.data.work_days || [0, 1, 2, 3, 4]);
    setHolidays(h.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (workshop?.name) setWsName(workshop.name);
  }, [workshop?.name]);

  const saveName = async (e) => {
    e.preventDefault();
    const trimmed = wsName.trim();
    if (!trimmed) { toast.error("Escribe un nombre para el taller"); return; }
    if (trimmed === workshop?.name) return;
    setSavingName(true);
    try {
      await api.patch("/workshop/me", { name: trimmed });
      await refresh();
      toast.success("Nombre del taller actualizado");
    } catch (e) {
      toast.error(errMsg(e, "No se pudo actualizar el nombre"));
    } finally {
      setSavingName(false);
    }
  };

  const toggleDay = (idx) => {
    setWorkDays((wd) => wd.includes(idx) ? wd.filter((d) => d !== idx) : [...wd, idx].sort());
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { ratio: ratio / 100, quick_ratio: quickRatio / 100, work_days: workDays });
      toast.success("Ajustes guardados");
    } catch (e) {
      toast.error(errMsg(e, "Error al guardar"));
    } finally {
      setSaving(false);
    }
  };

  const addHoliday = async (e) => {
    e.preventDefault();
    if (!hDate) { toast.error("Selecciona una fecha"); return; }
    try {
      await api.post("/holidays", { date: hDate, label: hLabel });
      toast.success("Día festivo añadido");
      setHDate(""); setHLabel("");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Error al añadir festivo"));
    }
  };

  const removeHoliday = async (date) => {
    if (!window.confirm("¿Quitar este día festivo?")) return;
    try {
      await api.delete(`/holidays/${date}`);
      toast.success("Festivo eliminado");
      load();
    } catch (e) {
      toast.error(errMsg(e, "No se pudo eliminar"));
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <header>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 flex items-center gap-3">
          <SlidersHorizontal className="w-8 h-8 text-blue-700" />
          Ajustes
        </h1>
        <p className="text-slate-500 mt-1">Ratio de capacidad, días laborables y festivos.</p>
      </header>

      <Card data-testid="workshop-name-card">
        <CardHeader>
          <CardTitle className="font-display text-xl flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-700" /> Datos del taller
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={saveName} className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="flex-1 space-y-2">
              <Label htmlFor="ws-name">Nombre del taller</Label>
              <Input
                id="ws-name"
                value={wsName}
                onChange={(e) => setWsName(e.target.value)}
                placeholder="Taller Pérez"
                maxLength={100}
                className="h-11"
                data-testid="workshop-name-input"
              />
              <p className="text-xs text-slate-500">Aparece en la cabecera, en los PDF y en los correos a tus clientes.</p>
            </div>
            <Button
              type="submit"
              disabled={savingName || !wsName.trim() || wsName.trim() === workshop?.name}
              className="h-11 bg-blue-700 hover:bg-blue-800 sm:w-auto"
              data-testid="save-workshop-name-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {savingName ? "Guardando..." : "Guardar nombre"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="font-display text-xl">Regla de capacidad · Mecánica General</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-slate-50 border border-slate-200 rounded-md p-6 text-center">
            <div className="text-sm text-slate-500 mb-2">Porcentaje programable</div>
            <div className="font-display text-6xl font-bold text-blue-700" data-testid="ratio-display">{ratio}%</div>
            <div className="text-sm text-slate-500 mt-2">
              Reserva del <span className="font-semibold text-amber-600">{100 - ratio}%</span> para imprevistos
            </div>
          </div>
          <div className="space-y-2">
            <Label>Ajustar porcentaje</Label>
            <Slider value={[ratio]} onValueChange={(v) => setRatio(v[0])} min={10} max={100} step={5} data-testid="ratio-slider" />
            <div className="flex justify-between text-xs text-slate-500">
              <span>10%</span><span>50%</span><span>100%</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="quick-ratio-card">
        <CardHeader>
          <CardTitle className="font-display text-xl">Regla de capacidad · Mecánica Rápida</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-amber-50 border border-amber-200 rounded-md p-6 text-center">
            <div className="text-sm text-slate-600 mb-2">Porcentaje programable</div>
            <div className="font-display text-6xl font-bold text-amber-600" data-testid="quick-ratio-display">{quickRatio}%</div>
            <div className="text-sm text-slate-500 mt-2">
              Reserva del <span className="font-semibold text-amber-700">{100 - quickRatio}%</span> para imprevistos
            </div>
          </div>
          <div className="space-y-2">
            <Label>Ajustar porcentaje</Label>
            <Slider value={[quickRatio]} onValueChange={(v) => setQuickRatio(v[0])} min={10} max={100} step={5} data-testid="quick-ratio-slider" />
            <div className="flex justify-between text-xs text-slate-500">
              <span>10%</span><span>50%</span><span>100%</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="font-display text-xl">Días laborables</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {DAYS.map((d) => (
              <label
                key={d.idx}
                className={`flex items-center gap-2 px-4 py-3 border rounded-md cursor-pointer transition-colors ${
                  workDays.includes(d.idx) ? "bg-blue-700 text-white border-blue-700" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                }`}
                data-testid={`workday-${d.idx}`}
              >
                <Switch checked={workDays.includes(d.idx)} onCheckedChange={() => toggleDay(d.idx)} />
                <span className="font-medium">{d.label}</span>
              </label>
            ))}
          </div>
        </CardContent>
      </Card>

      <div>
        <Button onClick={save} disabled={saving} className="h-12 bg-blue-700 hover:bg-blue-800 px-6" data-testid="save-settings-btn">
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Guardando..." : "Guardar ajustes"}
        </Button>
      </div>

      {/* Holidays */}
      <Card data-testid="holidays-card">
        <CardHeader>
          <CardTitle className="font-display text-xl flex items-center gap-2">
            <CalendarX className="w-5 h-5 text-red-600" /> Días festivos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500 mb-4">
            La app no programará citas en estos días.
          </p>
          <form onSubmit={addHoliday} className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <Input type="date" value={hDate} onChange={(e) => setHDate(e.target.value)} className="h-11" data-testid="holiday-date-input" />
            <Input placeholder="Descripción (opcional)" value={hLabel} onChange={(e) => setHLabel(e.target.value)} className="h-11" data-testid="holiday-label-input" />
            <Button type="submit" className="h-11 bg-red-600 hover:bg-red-700" data-testid="add-holiday-btn">
              <Plus className="w-4 h-4 mr-2" /> Añadir festivo
            </Button>
          </form>
          {holidays.length === 0 ? (
            <div className="text-slate-400 italic text-sm" data-testid="holidays-empty">Sin días festivos configurados.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {holidays.map((h) => (
                <li key={h.date} className="py-2 flex items-center justify-between" data-testid={`holiday-${h.date}`}>
                  <div>
                    <span className="font-medium text-slate-900">{h.date}</span>
                    {h.label && <span className="ml-3 text-sm text-slate-500">{h.label}</span>}
                  </div>
                  <Button variant="ghost" size="icon" className="h-9 w-9 text-red-600" onClick={() => removeHoliday(h.date)} data-testid={`delete-holiday-${h.date}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card className="border-red-200 bg-red-50/40">
        <CardHeader>
          <CardTitle className="font-display text-xl text-red-900 flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-red-700" />
            Zona peligrosa
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1">
            <div className="font-semibold text-red-900">Eliminar mi cuenta</div>
            <p className="text-sm text-red-800">Se borran de forma permanente todos los datos del taller. Esta acción es irreversible.</p>
          </div>
          <Link to="/eliminar-cuenta">
            <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-100" data-testid="open-delete-account">
              <Trash2 className="w-4 h-4 mr-2" /> Eliminar cuenta
            </Button>
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}
